import React, { useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { FamilyIdProvider } from './contexts/FamilyIdContext';
import { FamilyProvider } from './contexts/FamilyContext';
import { hasActiveChildSession } from './utils/childAuthHelper';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import FamilySetup from './pages/FamilySetup';
import SubscriptionPage from './pages/SubscriptionPage';
import ParentDashboard from './pages/ParentDashboard';
import ChildDashboard from './pages/ChildDashboard';
import ScreenTimeRules from './pages/ScreenTimeRules';
import FamilyMessages from './pages/FamilyMessages';
import CalendarPage from './pages/CalendarPage';
import ShoppingListPage from './pages/ShoppingListPage';
import PhotoGalleryPage from './pages/PhotoGalleryPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import COPPACompliance from './pages/COPPACompliance';
import ShareTargetPage from './pages/ShareTargetPage';
import SharedRecipePage from './pages/SharedRecipePage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import OnboardingChat from './pages/OnboardingChat';
import ConversationalChat from './pages/ConversationalChat';
import ContactPage from './pages/ContactPage';
import HelpPage from './pages/HelpPage';
import FAQPage from './pages/FAQPage';
import FeedbackPage from './pages/FeedbackPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import ProtectedRoute from './components/ProtectedRoute';
import FamilyRoute from './components/FamilyRoute';
import InstallPrompt from './components/InstallPrompt';
import UpdateNotification from './components/UpdateNotification';
import ErrorBoundary from './components/ErrorBoundary';
import CookieConsent from './components/CookieConsent';
import { onForegroundMessage } from './utils/notificationHelper';
import './App.css';
import './mobile-fixes.css';
import './pages/mobile-fixes.css';
import { Drawer, IconButton, List, ListItem, ListItemText, Box } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

// Create a beautiful, kid-friendly theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#4CAF50', // Green for success and growth
      light: '#81C784',
      dark: '#388E3C',
    },
    secondary: {
      main: '#FF9800', // Orange for energy and fun
      light: '#FFB74D',
      dark: '#F57C00',
    },
    success: {
      main: '#4CAF50',
    },
    warning: {
      main: '#FF9800',
    },
    error: {
      main: '#F44336',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      color: '#2E7D32',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
      color: '#388E3C',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
      color: '#2E7D32',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    // Accessibility: minimum 14px for form labels (mobile readability)
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
      },
    },
    // Play Store requirement: minimum 44px tap targets for icon buttons
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 48,
          padding: '10px',
        },
        sizeSmall: {
          minWidth: 44,
          minHeight: 48,
          padding: '8px',
        },
      },
    },
  },
});

function AppContent() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ width: 250, paddingTop: '64px' }} role="presentation">
      <List>
        {['Home', 'Blog', 'Login', 'Help', 'Features'].map((text, index) => (
          <ListItem button key={text} onClick={() => {
            navigate(text === 'Home' ? '/' : text.toLowerCase());
            setMobileOpen(false);
          }} sx={{ minHeight: '48px' }}>
            <ListItemText primary={text} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
  
  useEffect(() => {
    // Check for child session on mount
    const checkAuthType = () => {
      const authType = localStorage.getItem('earnE_authType');
      const isChildSession = hasActiveChildSession();
      
      if (isChildSession && authType === 'child') {
        // User has child session - navigate to child dashboard
        navigate('/child', { replace: true });
      }
    };
    
    checkAuthType();

    // Set up foreground message listener
    const unsubscribe = onForegroundMessage((payload) => {
      console.log('Received foreground message:', payload);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [navigate]);

  return (
    <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
        <AuthProvider>
          <FamilyIdProvider>
            <FamilyProvider>
              <div className="App">
                {/* Mobile Navigation - Hamburger Menu */}
                <IconButton
                  className="mobile-hamburger"
                  color="inherit"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ 
                    display: { xs: 'block', md: 'none' }, 
                    position: 'fixed', 
                    top: 16, 
                    right: 16, 
                    zIndex: 1000, 
                    bgcolor: 'white', 
                    boxShadow: 2,
                    minWidth: '48px !important',
                    minHeight: '48px !important',
                    padding: '12px !important',
                    '& .MuiSvgIcon-root': {
                      fontSize: '28px'
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>

                {/* Mobile Drawer Navigation */}
                <Drawer
                  anchor="right"
                  open={mobileOpen}
                  onClose={handleDrawerToggle}
                  sx={{ display: { xs: 'block', md: 'none' } }}
                >
                  {drawer}
                </Drawer>

                {/* PWA Components */}
                <InstallPrompt />
                <UpdateNotification />
                
                {/* Cookie Consent Banner */}
                <CookieConsent />
                
                <main role="main" style={{ display: 'contents' }}>
                <Routes>
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/share-target" element={<ShareTargetPage />} />
                  <Route path="/recipe/:recipeId" element={<SharedRecipePage />} />
                  <Route 
                    path="/family-setup" 
                    element={
                      <ProtectedRoute>
                        <FamilySetup />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/subscription" 
                    element={
                      <ProtectedRoute>
                        <FamilyRoute>
                          <SubscriptionPage />
                        </FamilyRoute>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/parent" 
                    element={
                      <ProtectedRoute>
                        <FamilyRoute>
                          <ParentDashboard />
                        </FamilyRoute>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/child" 
                    element={
                      <ProtectedRoute>
                        <FamilyRoute>
                          <ChildDashboard />
                        </FamilyRoute>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/calendar" 
                    element={
                      <ProtectedRoute>
                        <FamilyRoute>
                          <CalendarPage />
                        </FamilyRoute>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/shopping" 
                    element={
                      <ProtectedRoute>
                        <FamilyRoute>
                          <ShoppingListPage />
                        </FamilyRoute>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/gallery" 
                    element={
                      <ProtectedRoute>
                        <FamilyRoute>
                          <PhotoGalleryPage />
                        </FamilyRoute>
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="/screen-time"
                    element={
                      <ProtectedRoute>
                        <FamilyRoute>
                          <ScreenTimeRules />
                        </FamilyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/messages"
                    element={
                      <ProtectedRoute>
                        <FamilyRoute>
                          <FamilyMessages />
                        </FamilyRoute>
                      </ProtectedRoute>
                    }
                  />
                  {/* Legal Pages - No authentication required */}
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route path="/coppa-compliance" element={<COPPACompliance />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  {/* AI Onboarding Chat — no auth required */}
                  <Route path="/get-started" element={<OnboardingChat />} />
                  <Route path="/ai-chat" element={<ConversationalChat />} />
                  <Route path="/chat" element={<ConversationalChat />} />
                  {/* Public utility pages */}
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/feedback" element={<FeedbackPage />} />
                  <Route path="/analytics" element={
                    <ProtectedRoute>
                      <AnalyticsDashboard />
                    </ProtectedRoute>
                  } />
                  {/* Auth redirects for better UX */}
                  <Route path="/login" element={<Navigate to="/auth" replace />} />
                  <Route path="/signup" element={<Navigate to="/auth" replace />} />
                  <Route path="/" element={<LandingPage />} />
                  {/* Catch-all: redirect unknown routes to home */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </main>
              </div>
            </FamilyProvider>
          </FamilyIdProvider>
        </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
